'use client';

import { useState, useEffect, useCallback } from 'react';

const CEO_PIN = '5050';

interface Flag {
  id: string;
  key: string;
  name: string;
  description: string | null;
  flag_type: 'boolean' | 'percentage' | 'experiment';
  enabled: boolean;
  rollout_percentage: number;
  variants: Array<{ key: string; weight: number }>;
  target_user_types: string[];
  active: boolean;
  created_at: string;
  updated_at: string;
}

interface ExperimentStats {
  assignments: Record<string, number>;
  events: Record<string, number>;
}

const FLAG_TYPE_LABELS: Record<string, string> = {
  boolean: '🔘 On/Off',
  percentage: '📊 % Rollout',
  experiment: '🧪 A/B Test',
};

export default function ExperimentsPage() {
  const [flags, setFlags] = useState<Flag[]>([]);
  const [stats, setStats] = useState<Record<string, ExperimentStats>>({});
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newType, setNewType] = useState<string>('boolean');
  const [saving, setSaving] = useState(false);

  const headers = { 'x-ceo-pin': CEO_PIN, 'Content-Type': 'application/json' };

  const fetchFlags = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/feature-flags?pin=${CEO_PIN}`);
      const json = await res.json();
      setFlags(json.data || []);
      setStats(json.stats || {});
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFlags(); }, [fetchFlags]);

  const toggleActive = async (flag: Flag) => {
    await fetch('/api/feature-flags', {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ key: flag.key, active: !flag.active }),
    });
    fetchFlags();
  };

  const toggleEnabled = async (flag: Flag) => {
    await fetch('/api/feature-flags', {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ key: flag.key, enabled: !flag.enabled }),
    });
    fetchFlags();
  };

  const updateRollout = async (flag: Flag, pct: number) => {
    await fetch('/api/feature-flags', {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ key: flag.key, rollout_percentage: pct }),
    });
    fetchFlags();
  };

  const createFlag = async () => {
    if (!newKey.trim() || !newName.trim()) return;
    setSaving(true);
    await fetch('/api/feature-flags', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        key: newKey,
        name: newName,
        description: newDesc || null,
        flag_type: newType,
        variants: newType === 'experiment'
          ? [{ key: 'control', weight: 50 }, { key: 'variant_a', weight: 50 }]
          : [],
      }),
    });
    setNewKey('');
    setNewName('');
    setNewDesc('');
    setShowNew(false);
    setSaving(false);
    fetchFlags();
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white pb-20">
      <div className="sticky top-0 z-40 bg-gray-950/95 backdrop-blur border-b border-white/10 px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">🧪 Experiments & Flags</h1>
            <p className="text-xs text-gray-500">
              {flags.filter(f => f.active).length} active · {flags.length} total
            </p>
          </div>
          <button
            onClick={() => setShowNew(true)}
            className="text-sm bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-lg transition"
          >
            + New Flag
          </button>
        </div>
      </div>

      <div className="px-4 py-4 max-w-lg mx-auto space-y-3">
        {loading && (
          <div className="flex justify-center py-8">
            <div className="animate-spin w-6 h-6 border-2 border-white/20 border-t-white rounded-full" />
          </div>
        )}

        {/* New flag form */}
        {showNew && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-300">New Flag</span>
              <button onClick={() => setShowNew(false)} className="text-xs text-gray-500">Cancel</button>
            </div>
            <input
              value={newKey}
              onChange={(e) => setNewKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
              placeholder="flag_key (snake_case)"
              className="w-full rounded-md bg-gray-800 border border-white/10 px-2 py-1.5 text-sm text-gray-200 placeholder:text-gray-600 font-mono"
            />
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Display name"
              className="w-full rounded-md bg-gray-800 border border-white/10 px-2 py-1.5 text-sm text-gray-200 placeholder:text-gray-600"
            />
            <input
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="Description (optional)"
              className="w-full rounded-md bg-gray-800 border border-white/10 px-2 py-1.5 text-sm text-gray-200 placeholder:text-gray-600"
            />
            <select
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
              className="w-full rounded-md bg-gray-800 border border-white/10 px-2 py-1.5 text-xs text-gray-200"
            >
              <option value="boolean">🔘 Boolean (On/Off)</option>
              <option value="percentage">📊 Percentage Rollout</option>
              <option value="experiment">🧪 A/B Experiment</option>
            </select>
            <button
              onClick={createFlag}
              disabled={saving || !newKey.trim() || !newName.trim()}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-sm py-1.5 rounded-lg transition"
            >
              {saving ? 'Creating...' : 'Create Flag'}
            </button>
          </div>
        )}

        {/* Flag cards */}
        {!loading && flags.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <p className="text-4xl mb-3">🧪</p>
            <p className="text-sm">No feature flags yet</p>
          </div>
        )}

        {flags.map((flag) => {
          const flagStats = stats[flag.key];
          const totalAssigned = flagStats
            ? Object.values(flagStats.assignments).reduce((s, n) => s + n, 0)
            : 0;

          return (
            <div
              key={flag.id}
              className={`rounded-xl p-3 space-y-2 border ${
                flag.active
                  ? 'bg-white/5 border-white/10'
                  : 'bg-gray-500/5 border-gray-500/20 opacity-60'
              }`}
            >
              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{flag.name}</span>
                    <span className="text-xs text-gray-500">{FLAG_TYPE_LABELS[flag.flag_type]}</span>
                  </div>
                  <code className="text-xs text-gray-600 font-mono">{flag.key}</code>
                  {flag.description && (
                    <p className="text-xs text-gray-500 mt-0.5">{flag.description}</p>
                  )}
                </div>

                {/* Master toggle */}
                <button
                  onClick={() => toggleActive(flag)}
                  className={`shrink-0 w-12 h-6 rounded-full transition relative ${
                    flag.active ? 'bg-green-600' : 'bg-gray-700'
                  }`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${
                    flag.active ? 'left-6' : 'left-0.5'
                  }`} />
                </button>
              </div>

              {/* Controls based on type */}
              {flag.active && (
                <div className="pt-1 border-t border-white/5">
                  {flag.flag_type === 'boolean' && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">Enabled</span>
                      <button
                        onClick={() => toggleEnabled(flag)}
                        className={`text-xs px-3 py-1 rounded-lg transition ${
                          flag.enabled ? 'bg-green-500/20 text-green-300' : 'bg-gray-500/20 text-gray-400'
                        }`}
                      >
                        {flag.enabled ? '✅ On' : '⬜ Off'}
                      </button>
                    </div>
                  )}

                  {flag.flag_type === 'percentage' && (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400">Rollout</span>
                        <span className="text-xs font-medium">{flag.rollout_percentage}%</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="5"
                        value={flag.rollout_percentage}
                        onChange={(e) => updateRollout(flag, parseInt(e.target.value))}
                        className="w-full h-1.5 bg-gray-700 rounded-full appearance-none cursor-pointer accent-blue-500"
                      />
                      <div className="flex justify-between text-[10px] text-gray-600">
                        <span>0%</span>
                        <span>50%</span>
                        <span>100%</span>
                      </div>
                    </div>
                  )}

                  {flag.flag_type === 'experiment' && (
                    <div className="space-y-2">
                      <div className="text-xs text-gray-400">
                        Variants · {totalAssigned} users assigned
                      </div>
                      {flag.variants.map((v) => {
                        const assigned = flagStats?.assignments[v.key] || 0;
                        return (
                          <div key={v.key} className="flex items-center justify-between bg-white/5 rounded-lg px-2 py-1.5">
                            <div className="flex items-center gap-2">
                              <code className="text-xs font-mono text-gray-300">{v.key}</code>
                              <span className="text-[10px] text-gray-500">{v.weight}%</span>
                            </div>
                            <span className="text-xs text-gray-400">{assigned} users</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
